import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const UsersTab = ({ users, searchTerm, setSearchTerm, onRefresh }) => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [userData, setUserData] = useState({ name: '', email: '', password: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const filteredUsers = users.filter(
    (u) =>
      (u.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/admin/users/create`, userData);
      toast.success('Usuário criado com sucesso!');
      setShowUserForm(false);
      setUserData({ name: '', email: '', password: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      is_active: user.is_active,
      role: user.role,
      monthly_goal: user.monthly_goal || 16,
    });
  };

  const handleSaveUserEdit = async () => {
    try {
      await axios.patch(`${API}/api/admin/users/${editingUser.id}`, editUserData);
      toast.success('Usuário atualizado com sucesso!');
      setEditingUser(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Excluir o usuário "${userName}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      toast.success('Usuário excluído');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/api/admin/users/${userId}`, {
        is_active: !currentStatus,
      });
      toast.success(currentStatus ? 'Usuário desativado' : 'Usuário ativado');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao atualizar status do usuário');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    try {
      await axios.patch(`${API}/api/admin/users/${showPasswordReset.id}/password`, {
        new_password: newPassword,
      });
      toast.success('Senha resetada com sucesso!');
      setShowPasswordReset(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome ou email..."
            className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
            data-testid="search-users-input"
          />
        </div>
        <Button
          onClick={() => setShowUserForm(true)}
          className="bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80"
          data-testid="create-user-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Usuário
        </Button>
      </div>
      
      {showUserForm && (
        <motion.div
          className="bg-zinc-900/50 border border-neon-lime/30 backdrop-blur-md rounded-2xl p-6 mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-primary font-bold text-2xl text-white uppercase mb-4">
            Cadastrar Novo Usuário
          </h2>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Nome Completo</label>
              <Input
                type="text"
                value={userData.name}
                onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                placeholder="Ex: João Silva"
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
              <Input
                type="email"
                value={userData.email}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                placeholder="usuario@email.com"
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Senha</label>
              <Input
                type="password"
                value={userData.password}
                onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                placeholder="Senha temporária"
                className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                required
                minLength={6}
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80">
                Criar Usuário
              </Button>
              <Button type="button" onClick={() => setShowUserForm(false)} className="bg-zinc-800 text-white hover:bg-zinc-700">
                Cancelar
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-3">
        {filteredUsers.map((u) => (
          <motion.div
            key={u.id}
            className="bg-zinc-900/50 border border-white/5 backdrop-blur-md rounded-xl p-4 flex items-center justify-between"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            data-testid="user-row"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-bold text-lg">
                {(u.name || '?')[0]}
              </div>
              <div>
                <h3 className="font-secondary font-bold text-white">{u.name || 'Sem nome'}</h3>
                <p className="text-sm text-zinc-400">{u.email || ''}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-zinc-500">Nível {u.level}</span>
                  <span className="text-xs text-zinc-500">{u.total_xp} Pontos</span>
                  <span className="text-xs text-yellow-400 flex items-center gap-1">
                    <img src="/fercoin.png" alt="FC" className="w-4 h-4" />
                    {u.coins} FC
                  </span>
                  <span className="text-xs text-neon-purple">
                    Meta: {u.workouts_this_month || 0}/{u.monthly_goal || 16}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                u.is_active ? 'bg-utility-success/20 text-utility-success' : 'bg-utility-error/20 text-utility-error'
              }`}>
                {u.is_active ? 'Ativo' : 'Desativado'}
              </span>
              {u.role === 'admin' && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-neon-purple/20 text-neon-purple">Admin</span>
              )}
              <Button onClick={() => handleEditUser(u)} variant="ghost" className="text-zinc-400 hover:text-neon-lime p-2" title="Editar usuário">
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => toggleUserStatus(u.id, u.is_active)}
                className={`${u.is_active ? 'bg-orange-500 hover:bg-orange-600' : 'bg-utility-success hover:bg-utility-success/80'} text-white font-bold text-sm px-3`}
                data-testid="toggle-user-button"
              >
                {u.is_active ? 'Desativar' : 'Ativar'}
              </Button>
              <Button
                onClick={() => handleDeleteUser(u.id, u.name || 'Sem nome')}
                className="bg-red-500 hover:bg-red-600 text-white font-bold text-sm px-3"
                data-testid="delete-user-button"
              >
                Excluir
              </Button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal de Edição de Usuário */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-zinc-900 border border-neon-lime/30 rounded-2xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-primary font-bold text-xl text-white">Editar Usuário</h3>
              <Button onClick={() => setEditingUser(null)} variant="ghost" className="text-zinc-400 hover:text-white p-2">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nome</label>
                <Input
                  type="text"
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                  className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
                <Input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                  className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Status</label>
                <select
                  value={editUserData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setEditUserData({ ...editUserData, is_active: e.target.value === 'active' })}
                  className="w-full h-12 bg-zinc-950/50 border border-zinc-800 rounded-lg text-white px-3"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Desativado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Meta 30 Dias (treinos/mês)</label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={editUserData.monthly_goal}
                  onChange={(e) => setEditUserData({ ...editUserData, monthly_goal: parseInt(e.target.value) || 16 })}
                  className="bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                  data-testid="monthly-goal-input"
                />
                <p className="text-xs text-zinc-500 mt-1">Quantos treinos o aluno deve fazer em 30 dias</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Tipo de Usuário</label>
                <select
                  value={editUserData.role}
                  onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                  className="w-full h-12 bg-zinc-950/50 border border-zinc-800 rounded-lg text-white px-3"
                >
                  <option value="participant">Participante</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={handleSaveUserEdit} className="flex-1 bg-neon-lime text-black font-black uppercase hover:bg-neon-lime/80">
                  <Save className="w-5 h-5 mr-2" />
                  Salvar
                </Button>
                <Button onClick={() => setShowPasswordReset(editingUser)} className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                  Resetar Senha
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal de Reset de Senha */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-zinc-900 border border-orange-500/30 rounded-2xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-primary font-bold text-xl text-white">Resetar Senha</h3>
              <Button onClick={() => { setShowPasswordReset(null); setNewPassword(''); }} variant="ghost" className="text-zinc-400 hover:text-white p-2">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <p className="text-zinc-400 text-sm mb-4">
              Definir nova senha para <span className="text-white font-bold">{showPasswordReset.name}</span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nova Senha</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="bg-zinc-950/50 border-zinc-800 focus:border-orange-500 text-white h-12"
                  minLength={6}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleResetPassword}
                  disabled={newPassword.length < 6}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold disabled:opacity-50"
                >
                  Confirmar Reset
                </Button>
                <Button onClick={() => { setShowPasswordReset(null); setNewPassword(''); }} className="bg-zinc-800 text-white hover:bg-zinc-700">
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
